<script setup lang="ts">
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Props for the UnsavedChangesDialog component
 * @param {boolean} visible - Controls the visibility of the dialog
 */
interface Props {
  visible: boolean;
}

const props = defineProps<Props>();

/**
 * Emits for the UnsavedChangesDialog component
 * @event {[]boolean} update:visible - Emitted when the visibility of the dialog changes
 * @event {} discard - Emitted when the user confirms to discard changes
 * @event {} cancel - Emitted when the user cancels the dialog or clicks X
 * @event {} save - Emitted when the user wants to save changes
 */
const emit = defineEmits<{
  'update:visible': [visible: boolean];
  'discard': [];
  'cancel': [];
  'save': [];
}>();

const { t } = useI18n();

// Local state for the dialog visibility
const dialogVisible = ref(props.visible);

// Synchronize local state with the prop
watch(() => props.visible, (newValue) => {
  dialogVisible.value = newValue;
});

// Synchronize changes back to the parent
watch(dialogVisible, (newValue) => {
  if (newValue !== props.visible) {
    emit('update:visible', newValue);
  }
});

/**
 * This function emits the 'discard' event to the parent component
 */
function handleDiscard() {
  emit('discard');
  dialogVisible.value = false;
}

/**
 * This function emits the 'cancel' event to the parent component
 */
function handleCancel() {
  emit('cancel');
  dialogVisible.value = false;
}

/**
 * This function emits the 'save' event to the parent component
 */
function handleSave() {
  emit('save');
  dialogVisible.value = false;
}
</script>

<template>
  <Dialog
    :visible="dialogVisible"
    @update:visible="dialogVisible = $event"
    :header="t('common.dialogs.unsavedChanges.title')"
    @hide="handleCancel"
    @keydown.enter.prevent="handleSave"
  >
    <div class="p-3">
      {{ t('common.dialogs.unsavedChanges.message') }}
    </div>
    <template #footer>
      <div class="flex w-full justify-content-between">
        <Button
          :label="t('common.actions.save')"
          @click="handleSave"
          severity="success"
          autofocus
        />
        <Button
          :label="t('common.actions.discard')"
          severity="danger"
          @click="handleDiscard"
        />
      </div>
    </template>
  </Dialog>
</template>
